import { claimQueueManager } from "@/services/claim-queue/claim-queue-manager";
import { cacheManager } from "@/services/pwa/cache-manager/cache-manager";
import { storageManager } from "@/services/storage/storage/storage";
import { captureException } from "@/utils/sentry";
import * as Sentry from "@sentry/react";

type StorageHealthMonitorOptions = {
  pollIntervalMs?: number;
  getVisibilityState?: () => DocumentVisibilityState;
  getCacheStorage?: () => CacheStorage | undefined;
  getNavigator?: () => Pick<Navigator, "storage">;
};

const POLL_INTERVAL_MS = 5 * 60 * 1000;

export class StorageHealthMonitor {
  private isMonitoring = false;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private readonly pollIntervalMs: number;
  private readonly getVisibilityState: () => DocumentVisibilityState;
  private readonly getCacheStorage: () => CacheStorage | undefined;
  private readonly getNavigator: () => Pick<Navigator, "storage">;

  constructor(options: StorageHealthMonitorOptions = {}) {
    this.pollIntervalMs = options.pollIntervalMs ?? POLL_INTERVAL_MS;
    this.getVisibilityState =
      options.getVisibilityState ??
      (() =>
        typeof document !== "undefined" ? document.visibilityState : "visible");
    this.getCacheStorage =
      options.getCacheStorage ??
      (() => (typeof caches !== "undefined" ? caches : undefined));
    this.getNavigator = options.getNavigator ?? (() => navigator);
  }

  public start(): void {
    if (this.isMonitoring) {
      return;
    }

    this.isMonitoring = true;
    this.intervalId = setInterval(() => {
      void this.poll();
    }, this.pollIntervalMs);
  }

  public stop(): void {
    if (!this.isMonitoring) {
      return;
    }

    this.isMonitoring = false;

    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  public async poll(): Promise<void> {
    if (this.getVisibilityState() === "hidden") {
      return;
    }

    try {
      const nav = this.getNavigator();
      const cacheStorage = this.getCacheStorage();

      const storageEstimatePromise = nav.storage?.estimate
        ? nav.storage.estimate()
        : Promise.resolve({});

      const [snapshots, stats, claimCount, storageEstimate] = await Promise.all(
        [
          storageManager.getSnapshotCacheStats(),
          cacheManager.getStorageStats(),
          claimQueueManager.count(),
          storageEstimatePromise,
        ]
      );

      const totalCachedItems = await this.readCachedItemCount(
        cacheStorage,
        stats.caches.names
      );

      Sentry.metrics.gauge("idb.snapshots.count", snapshots.count, {
        unit: "none",
      });
      Sentry.metrics.gauge(
        "idb.snapshots.bytes_mb",
        snapshots.totalBytes / 1024 / 1024,
        { unit: "megabyte" }
      );
      Sentry.metrics.gauge("idb.claim_queue", claimCount, { unit: "none" });
      Sentry.metrics.gauge(
        "idb.pending_requests",
        stats.indexedDB.pendingRequests,
        { unit: "none" }
      );
      Sentry.metrics.gauge("idb.offline_data", stats.indexedDB.offlineData, {
        unit: "none",
      });

      const usedBytes = (storageEstimate as StorageEstimate).usage ?? 0;
      const quotaBytes = (storageEstimate as StorageEstimate).quota ?? 0;
      Sentry.metrics.gauge("storage.used_mb", usedBytes / 1024 / 1024, {
        unit: "megabyte",
      });
      Sentry.metrics.gauge("storage.quota_mb", quotaBytes / 1024 / 1024, {
        unit: "megabyte",
      });
      Sentry.metrics.gauge("sw.cached_items", totalCachedItems, {
        unit: "none",
      });
    } catch (error) {
      captureException(error);
    }
  }

  private async readCachedItemCount(
    cacheStorage: CacheStorage | undefined,
    cacheNames: string[]
  ): Promise<number> {
    if (!cacheStorage) {
      return 0;
    }
    const counts = await Promise.all(
      cacheNames.map(async (name) => {
        const cache = await cacheStorage.open(name);
        const keys = await cache.keys();
        return keys.length;
      })
    );
    return counts.reduce((a, b) => a + b, 0);
  }
}

export const storageHealthMonitor = new StorageHealthMonitor();
